import * as React from 'react'

import {
  ChevronsDownUpIcon,
  ChevronsUpDownIcon,
  CircleAlertIcon,
} from 'lucide-react'
import { inject, observer, useLocalObservable } from 'mobx-react'
import { classList } from 'react-classlist-helper'

import DesktopInitialState from '~/client/src/desktop/stores/DesktopInitialState'
import Avatar, {
  AvatarSize,
} from '~/client/src/shared/components/Avatar/Avatar'
import { ListHeader } from '~/client/src/shared/components/ListHeader'
import VirtualMultiGrid from '~/client/src/shared/components/ListWithFixedColumns/VirtualMultiGrid'
import { Button } from '~/client/src/shared/components/shadcn-ui/button'
import { Checkbox } from '~/client/src/shared/components/shadcn-ui/checkbox'
import TooltipWrapper from '~/client/src/shared/components/TooltipWrapper/TooltipWrapper'
import UserListFilters from '~/client/src/shared/components/UserListSelector/UserListFilters'
import AvatarType from '~/client/src/shared/enums/AvatarType'
import Localization from '~/client/src/shared/localization/LocalizationManager'
import Workflow from '~/client/src/shared/models/Workflow'
import CompaniesStore from '~/client/src/shared/stores/domain/Companies.store'
import ProjectMembersStore from '~/client/src/shared/stores/domain/ProjectMembers.store'
import ProjectRolesStore from '~/client/src/shared/stores/domain/ProjectRoles.store'
import ProjectTeamsStore from '~/client/src/shared/stores/domain/ProjectTeams.store'
import ProjectTradesStore from '~/client/src/shared/stores/domain/ProjectTrades.store'
import SitePermitAssignmentsStore from '~/client/src/shared/stores/domain/SitePermitAssignments.store'
import TagsStore from '~/client/src/shared/stores/domain/Tags.store'
import UserProjectsStore from '~/client/src/shared/stores/domain/UserProjects.store'
import WorkflowTypesStore from '~/client/src/shared/stores/domain/WorkflowTypes.store'

import {
  getSubscribeUsersGroupingOptions,
  getSubscribeUsersSortingOptions,
} from './subscribeUsersOptions'
import SubscribeUsersSelectorStore, {
  SUBSCRIBE_USERS_FILTER_TAG_TYPES,
  SubscribeUsersSelectorDataKeys,
  SubscribeUsersSelectorGroupingOption,
  SubscribeUsersSelectorTab,
} from './SubscribeUsersSelector.store'

interface IProps {
  selectedWorkflows: Workflow[]
  projectMembersStore?: ProjectMembersStore
  state?: DesktopInitialState
  companiesStore?: CompaniesStore
  userProjectsStore?: UserProjectsStore
  workflowAssignmentsStore?: SitePermitAssignmentsStore
  workflowTypesStore?: WorkflowTypesStore
  projectRolesStore?: ProjectRolesStore
  projectTeamsStore?: ProjectTeamsStore
  projectTradesStore?: ProjectTradesStore
  tagsStore?: TagsStore
  onClose?: () => void
}

const SubscribeUsersSelectorComponent: React.FC<IProps> = ({
  projectMembersStore,
  userProjectsStore,
  companiesStore,
  state,
  workflowAssignmentsStore,
  selectedWorkflows,
  workflowTypesStore,
  projectRolesStore,
  projectTeamsStore,
  projectTradesStore,
  tagsStore,
  onClose,
}) => {
  const store = useLocalObservable(
    () =>
      new SubscribeUsersSelectorStore(
        projectMembersStore,
        userProjectsStore,
        companiesStore,
        state,
        selectedWorkflows,
        workflowAssignmentsStore,
        projectRolesStore,
        projectTeamsStore,
        projectTradesStore,
        tagsStore,
      ),
  )

  const {
    searchKey,
    rows,
    columns,
    collapsedCategories,
    setSearch,
    toggleCategoryCollapsing,
    toggleCategoryAssingment,
    toggleUserAssingment,
    activeTab,
    setActiveTab,
    subscribedCount,
    areAllVisibleUsersAssignedToAll,
    toggleSelectAllVisibleUsers,
    isAllCollapsed,
    expandAllCategories,
    collapseAllCategories,
  } = store

  const sortingOptions = React.useMemo(getSubscribeUsersSortingOptions, [])
  const groupingOptions = React.useMemo(getSubscribeUsersGroupingOptions, [])

  const handleGroupChange = React.useCallback(
    (key: string | null) => {
      store.setGroupingKey(
        (key as SubscribeUsersSelectorGroupingOption) ??
          SubscribeUsersSelectorGroupingOption.NONE,
      )
    },
    [store],
  )

  const buildPartialSubscriptionTooltip = React.useCallback(
    (userId: string): JSX.Element => {
      const subscribedWorkflows = (selectedWorkflows || []).filter(workflow =>
        store.isAssigned(workflow.id, userId),
      )
      const nonSubscribedWorkflows = (selectedWorkflows || []).filter(
        workflow => !store.isAssigned(workflow.id, userId),
      )

      return (
        <div className="tw:text-body-sm tw:text-primary-foreground">
          <div className="tw:mb-2">
            {
              Localization.translator
                .thisUserHasSubscribedToOnlySomeOfTheWorkflowsYouSelected
            }
          </div>
          {!!subscribedWorkflows.length && (
            <div className="tw:mb-2">
              <div className="tw:font-semibold tw:mb-1">
                {Localization.translator.subscribedTo}
              </div>
              {subscribedWorkflows.map(workflow => (
                <div key={`s-${workflow.id}`}>
                  {workflow.getCaption(workflowTypesStore)}
                </div>
              ))}
            </div>
          )}
          {!!nonSubscribedWorkflows.length && (
            <div>
              <div className="tw:font-semibold tw:mb-1">
                {Localization.translator.notSubscribedTo}
              </div>
              {nonSubscribedWorkflows.map(workflow => (
                <div key={`n-${workflow.id}`}>
                  {workflow.getCaption(workflowTypesStore)}
                </div>
              ))}
            </div>
          )}
        </div>
      )
    },
    [workflowTypesStore, selectedWorkflows, store],
  )

  return (
    <div className="tw:min-w-[400px] tw:max-w-[400px] tw:max-h-[600px] tw:min-h-[400px] tw:flex-1 tw:flex tw:flex-col tw:overflow-hidden">
      <div className="tw:flex tw:items-center tw:[&>*]:flex-1 tw:justify-between tw:px-2.5 tw:mb-2.5 tw:border-0 tw:border-b tw:border-solid tw:border-border">
        <div
          className={classList({
            'tw:relative tw:border-b-0 tw:cursor-pointer tw:py-3 tw:text-body-md tw:text-foreground tw:text-center': true,
            'tw:text-primary tw:after:content-[""] tw:after:absolute tw:after:left-1/2 tw:after:bottom-0 tw:after:-translate-x-1/2 tw:after:w-[55px] tw:after:h-[3px] tw:after:bg-primary tw:after:rounded-t-full':
              activeTab === SubscribeUsersSelectorTab.All,
          })}
          onClick={() => setActiveTab(SubscribeUsersSelectorTab.All)}
        >
          {Localization.translator.allUsers}
        </div>
        <div
          className={classList({
            'tw:relative tw:border-b-0 tw:cursor-pointer tw:py-3 tw:text-body-md tw:text-foreground tw:text-center': true,
            'tw:text-primary tw:after:content-[""] tw:after:absolute tw:after:left-1/2 tw:after:bottom-0 tw:after:-translate-x-1/2 tw:after:w-[55px] tw:after:h-[3px] tw:after:bg-primary tw:after:rounded-t-full':
              activeTab === SubscribeUsersSelectorTab.Subscribed,
          })}
          onClick={() => setActiveTab(SubscribeUsersSelectorTab.Subscribed)}
        >
          {`${Localization.translator.subscribed} (${subscribedCount})`}
        </div>
      </div>

      <ListHeader.DisplayBar
        search={{ value: searchKey, onChange: setSearch }}
        quickFilters={{
          isOnlyMe: store.isOnlyMeActive,
          isOnlyMyCompany: store.isOnlyMyCompanyActive,
          onToggleMe: store.toggleOnlyMe,
          onToggleMyCompany: store.toggleOnlyMyCompany,
        }}
        filter={
          <UserListFilters
            tagsByType={tagsStore?.tagListsWithDefaultsTagsMap || {}}
            tagTypes={SUBSCRIBE_USERS_FILTER_TAG_TYPES}
            appliedTagIds={store.appliedTagIds}
            onApplyFilters={store.setSelectedTagIds}
          />
        }
        grouping={{
          value:
            store.groupingKey === SubscribeUsersSelectorGroupingOption.NONE
              ? null
              : store.groupingKey,
          defaultValue: null,
          options: groupingOptions,
          onChange: handleGroupChange,
        }}
        sorting={{
          value: {
            key: store.sortState.columnKey || null,
            order: store.sortState.order,
          },
          defaultValue: { key: null },
          options: sortingOptions,
          onChange: value => store.setSort(value.key, value.order),
        }}
      />
      <div className="tw:flex tw:items-center tw:px-3 tw:py-2.5 tw:border-0 tw:border-b tw:border-solid tw:border-border">
        <span className="tw:mr-2">
          <Checkbox
            checked={areAllVisibleUsersAssignedToAll}
            onCheckedChange={() => toggleSelectAllVisibleUsers()}
          />
        </span>
        {isAllCollapsed ? (
          <Button
            variant="ghost"
            size="icon-xs"
            title={Localization.translator.expandAll}
            aria-label={Localization.translator.expandAll}
            className="tw:mr-2 tw:text-muted-foreground"
            onClick={expandAllCategories}
          >
            <ChevronsUpDownIcon className="tw:size-4" />
          </Button>
        ) : (
          <Button
            variant="ghost"
            size="icon-xs"
            title={Localization.translator.collapseAll}
            aria-label={Localization.translator.collapseAll}
            className="tw:mr-2 tw:text-muted-foreground"
            onClick={collapseAllCategories}
          >
            <ChevronsDownUpIcon className="tw:size-4" />
          </Button>
        )}
        <span className="tw:text-body-sm tw:text-muted-foreground">
          {Localization.translator.xUsers(store.filteredCollection.length)}
        </span>
      </div>

      <div className="tw:flex-1 tw:flex tw:flex-col tw:overflow-hidden">
        {rows.length > 0 && (
          <VirtualMultiGrid
            rows={rows}
            measureRowHeight
            noHeader
            columns={columns}
            fixedColumnsCount={1}
            dataCellClass="tw:!pl-0"
            className="tw:!overflow-x-hidden"
            onCategoryCheckboxToggle={toggleCategoryAssingment}
            cellRenderer={(columnKey, value) => {
              switch (columnKey) {
                case SubscribeUsersSelectorDataKeys.CHECKBOX:
                  return (
                    <Checkbox
                      checked={value.isAssignedToAll}
                      indeterminate={
                        value.isAssignedToAny && !value.isAssignedToAll
                      }
                    />
                  )
                case SubscribeUsersSelectorDataKeys.USER: {
                  const user = value
                  const isAny = store.isUserAssignedToAnySelectedWorkflow(
                    user.id,
                  )
                  const isAll = store.isUserAssignedToAllSelectedWorkflows(
                    user.id,
                  )
                  const shouldShowInfo = isAny && !isAll

                  return (
                    <div className="tw:flex tw:items-center tw:[&>*]:flex-none tw:w-full">
                      <Avatar
                        src={user.avatarUrl}
                        size={AvatarSize.Minuscule}
                        avatarType={AvatarType.User}
                        className="tw:mr-2"
                      />
                      <span className="tw:overflow-hidden tw:whitespace-nowrap tw:text-ellipsis">
                        {user.fullName}
                      </span>
                      {shouldShowInfo && (
                        <TooltipWrapper
                          className="tw:flex tw:items-center tw:grow-0 tw:ml-auto tw:mr-2"
                          content={buildPartialSubscriptionTooltip(user.id)}
                          side="bottom"
                          align="start"
                        >
                          <CircleAlertIcon className="tw:size-4" />
                        </TooltipWrapper>
                      )}
                    </div>
                  )
                }
                default:
                  return null
              }
            }}
            onCellClick={rowData => {
              toggleUserAssingment(rowData.id)
            }}
            collapsedCategories={collapsedCategories}
            onCategoryCollapsingToggle={toggleCategoryCollapsing}
          />
        )}

        {rows.length === 0 && (
          <div className="tw:text-center tw:py-3 tw:text-body-md tw:text-foreground">
            {Localization.translator.noUsersHaveSubscribedYet}
          </div>
        )}
      </div>
      <div className="tw:flex tw:justify-end tw:gap-2 tw:p-3">
        <Button
          variant="primary-outline"
          size="lg"
          onClick={() => {
            store.discardChanges()
            onClose?.()
          }}
        >
          {Localization.translator.cancel}
        </Button>
        <Button
          variant="default"
          size="lg"
          disabled={!(store.hasPendingChanges && !!selectedWorkflows?.length)}
          onClick={() => {
            store.applyChanges()
            onClose?.()
          }}
        >
          {Localization.translator.apply}
        </Button>
      </div>
    </div>
  )
}

const SubscribeUsersSelector = inject(
  'projectMembersStore',
  'state',
  'userProjectsStore',
  'companiesStore',
  'workflowAssignmentsStore',
  'workflowTypesStore',
  'projectRolesStore',
  'projectTeamsStore',
  'projectTradesStore',
  'tagsStore',
)(observer(SubscribeUsersSelectorComponent))

export default SubscribeUsersSelector
